import { DateRange } from '@/components/DateRangePicker';
import {
    AGENT_TYPE,
    AgentConversation,
    EVENT_TYPES, MessageType, ROLES, STATUSES
} from '@common/api/agentService';
import { supabase } from '@common/api/authService';
import dayjs from 'dayjs';
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef, useState
} from 'react';

// Types
export interface KPI {
  id?: string;
  layout?: { x: number; y: number; w: number; h: number };
  title: string;
  sql: string;
  sql_detail?: string;
  formula: string;
  column: string;
  format: 'number' | 'currency' | 'percent' | 'string';
  unit: string;
  data: DataObj;
  prevData?: DataObj;
  loading?: boolean;
  sources?: string[]; // 数据源列表,如 ['Shopify', 'Google Ads']
}

export type DataObj = Record<string, (number | string)>[];

export interface ChartConfig {
  cartesian?: {
      xAxis?: {
          field?: string;
          type?: 'category' | 'value' | 'time' | 'log';
          name?: string;
          axisLabel?: {
              formatter?: string;
          };
      };
      yAxis?: {
          type?: 'value' | 'category' | 'log';
          name?: string;
          axisLabel?: {
              formatter?: string;
          };
      }[];
      series?: {
          type: 'line' | 'bar' | 'scatter' | 'heatmap';
          name?: string;
          encode?: {
              x?: string;
              y?: string;
              value?: string;
          };
          yAxisIndex?: number;
          stack?: string;
          label?: {
              formatter?: string;
          };
      }[];
  };
  pie?: {
      series?: {
          type: 'pie';
          name?: string;
          encode?: {
              itemName?: string;
              value?: string;
          };
          label?: {
              formatter?: string;
          };
      }[];
  };
  radar?: {
      indicator?: { name: string; max: number; field?: string }[];
      series?: {
          type: 'radar';
          name?: string;
          encode?: {
              itemName?: string;
          };
      }[];
  };
  funnel?: {
      series?: {
          type: 'funnel';
          name?: string;
          encode?: {
              itemName?: string;
              value?: string;
          };
          label?: {
              formatter?: string;
          };
      }[];
  };
  gauge?: {
      series?: {
          type: 'gauge';
          name?: string;
          encode?: {
              value?: string;
          };
          axisLabel?: {
              formatter?: string;
          };
      }[];
  };
  sankey?: {
      series?: {
          type: 'sankey';
          name?: string;
          encode?: {
              source?: string;
              target?: string;
              value?: string;
          };
      }[];
  };
}

export interface Chart {
  id: string;
  layout?: { x: number; y: number; w: number; h: number };
  chart_config: ChartConfig;
  sql_detail?: string;
  title: string;
  data: DataObj; // dataset path or inline data
  sql: string;
  formula: string;
  loading?: boolean;
  sources?: string[]; // 数据源列表,如 ['Shopify', 'Google Ads']
}

export interface DashboardSchema {
  kpi?: KPI[];
  chart?: Chart[];
}

export type OperationType = 'ADD' | 'DELETE' | 'UPDATE' | 'IMPORT';

export interface PatchOperation {
    op: OperationType;
    path: string; // e.g. "kpi[0].layout.x" or "chart"
    value?: any;
}

export type DashboardActionType = 'BATCH_PATCH';

export interface DashboardAction {
    type: DashboardActionType;
    payload: PatchOperation[];
    description: string;
    timestamp: number;
}

export interface EditorState {
    originalSchema: DashboardSchema;
    currentSchema: DashboardSchema;
    actions: DashboardAction[];
    pointer: number; // Index of the last applied action
    isDirty: boolean;
}

export interface Dashboard {
    id?: string;
    title: string;
    schema: DashboardSchema;
    created_at: string;
    dateRange: DateRange;
    conversation_id?: string;
    sort_key?: number;
}

export interface DashboardState {
    /** 消息管理 */
    messages: MessageType[];
    setMessages: React.Dispatch<React.SetStateAction<MessageType[]>>;
    addMessage: (message: MessageType) => void;

    /** 生成状态 */
    isGenerating: boolean;
    setIsGenerating: React.Dispatch<React.SetStateAction<boolean>>;

    showGenerating: boolean;
    setShowGenerating: React.Dispatch<React.SetStateAction<boolean>>;

    /** AbortController 相关 */
    abortControllerRef: React.RefObject<AbortController | null>;
    cancelCurrentRequest: () => void;
    createNewAbortController: () => AbortController;

    /** 错误处理 */
    error: string | null;
    setError: React.Dispatch<React.SetStateAction<string | null>>;
    
    /** 版本管理 */
    history: MessageType[];
    setHistory: React.Dispatch<React.SetStateAction<MessageType[]>>;
    selectedVersion: Dashboard | null;
    setSelectedVersion: React.Dispatch<React.SetStateAction<Dashboard | null>>;
    versions: Dashboard[];
    setVersions: React.Dispatch<React.SetStateAction<Dashboard[]>>;
    isLoadingVersions: boolean;
    setIsLoadingVersions: React.Dispatch<React.SetStateAction<boolean>>;

    /** Editor State Management */
    editorStates: Record<string, EditorState>;
    initEditor: (dashboardId: string, schema: DashboardSchema) => void;
    dispatchAction: (dashboardId: string, action: Omit<DashboardAction, 'timestamp'>) => void;
    updateData: (dashboardId: string, schema: DashboardSchema) => void;
    undo: (dashboardId: string) => void;
    redo: (dashboardId: string) => void;
    saveDashboard: (dashboardId: string, title?: string, conversationId?: string) => Promise<void>;
    resetEditor: (dashboardId: string) => void;
    deleteDashboard: (dashboardId: string) => Promise<void>;
    renameDashboard: (id: string, newName: string) => Promise<void>;
    reorderDashboards: (newOrder: Dashboard[], movedId?: string, newIndex?: number) => Promise<void>;

    /** 创建模态框 */
    showCreateModal: boolean;
    setShowCreateModal: (show: boolean) => void;

    /** 拉取 dashboard 列表 */
    getDashboards: () => Promise<void>;

    /** 会话列表管理 */
    conversations: AgentConversation[];
    setConversations: React.Dispatch<React.SetStateAction<AgentConversation[]>>;
    currentConversationId: string | null;
    setCurrentConversationId: React.Dispatch<React.SetStateAction<string | null>>;
    isLoadingConversations: boolean;
    isLoadingMessages: boolean;
    getConversations: (dashboardId?: string) => Promise<void>;
    deleteConversation: (id: string) => Promise<void>;
    updateConversation: (id: string, title: string) => Promise<void>;
    getMessages: (conversationId: string) => Promise<void>;

    /** UI 状态 */
    isEditMode: boolean;
    setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
    
    localSchema: DashboardSchema | undefined;
    setLocalSchema: React.Dispatch<React.SetStateAction<DashboardSchema | undefined>>;

    /** 自动滚动 */
    activeItemId: string | null;
    setActiveItemId: React.Dispatch<React.SetStateAction<string | null>>;
}

const initialState: DashboardState = {
    messages: [],
    setMessages: () => {},
    addMessage: () => {},
    isGenerating: false,
    setIsGenerating: () => {},
    showGenerating: false,
    setShowGenerating: () => {},
    abortControllerRef: { current: null },
    cancelCurrentRequest: () => {},
    createNewAbortController: () => new AbortController(),
    error: null,
    setError: () => {},
    selectedVersion: null,
    setSelectedVersion: () => {},
    history: [],
    setHistory: () => {},
    versions: [],
    setVersions: () => {},
    isLoadingVersions: false,
    setIsLoadingVersions: () => {},
    
    editorStates: {},
    initEditor: () => {},
    dispatchAction: () => {},
    updateData: () => {},
    undo: () => {},
    redo: () => {},
    saveDashboard: async (_dashboardId: string, _title?: string, _conversationId?: string) => {},
    resetEditor: () => {},
    deleteDashboard: async () => {},
    renameDashboard: async () => {},
    reorderDashboards: async () => {},

    showCreateModal: false,
    setShowCreateModal: () => {},
    getDashboards: async () => {},

    conversations: [],
    setConversations: () => {},
    currentConversationId: null,
    setCurrentConversationId: () => {},
    isLoadingConversations: false,
    isLoadingMessages: false,
    getConversations: async (_dashboardId?: string) => {},
    deleteConversation: async () => {},
    updateConversation: async () => {},
    getMessages: async () => {},

    isEditMode: false,
    setIsEditMode: () => {},

    localSchema: undefined,
    setLocalSchema: () => {},

    activeItemId: null,
    setActiveItemId: () => {},
};

export { initialState as dashboardInitialState };

const DashboardContext = createContext<DashboardState>(initialState);

export const useDashboardContext = () => useContext(DashboardContext);

// Minimal lodash replacement for avoiding type issues
const _get = (obj: any, path: string, defaultValue?: any) => {
    const keys = path.split(/[\.\[\]]/).filter(Boolean);
    let result = obj;
    for (const key of keys) {
        if (result === undefined || result === null) return defaultValue;
        result = result[key];
    }
    return result === undefined ? defaultValue : result;
};

const _set = (obj: any, path: string, value: any) => {
    const keys = path ? path.split(/[\.\[\]]/).filter(Boolean) : [];
    
    if (keys.length === 0) {
        if (obj && typeof obj === 'object') {
            if (Array.isArray(obj)) {
                obj.length = 0;
            } else {
                for (const key in obj) {
                    if (Object.prototype.hasOwnProperty.call(obj, key)) {
                        delete obj[key];
                    }
                }
            }
            
            if (value && typeof value === 'object') {
                Object.assign(obj, value);
            }
        }
        return;
    }

    let current = obj;
    for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (current[key] === undefined || current[key] === null) {
            // Check if next key is an integer to decide array vs object
            current[key] = /^\d+$/.test(keys[i + 1]) ? [] : {};
        }
        current = current[key];
    }
    current[keys[keys.length - 1]] = value;
};

const _unset = (obj: any, path: string) => {
    if (!path) return;
    const keys = path.split(/[\.\[\]]/).filter(Boolean);
    if (keys.length === 0) return;

    let current = obj;
    for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (current[key] === undefined || current[key] === null) return;
        current = current[key];
    }
    const lastKey = keys[keys.length - 1];
    if (Array.isArray(current)) {
        // Note: delete on array leaves hole, splice is better but unset is generic
        // We handle splice in applyPatch
        current.splice(parseInt(lastKey, 10), 1);
    } else {
        delete current[lastKey];
    }
};

// Helper to apply patches
const applyPatch = (obj: any, operation: PatchOperation) => {
    const {
        op, path, value 
    } = operation;
    
    // ADD: Can be array push OR object set key (if path ends in key)
    // If path targets an array directly (e.g. "kpi"), we assume PUSH.
    // If path targets a non-existent key, we SET it.
    if (op === 'ADD') {
        // Check if we are targeting an array itself
        const target = _get(obj, path);
        if (Array.isArray(target)) {
            target.push(value);
            return;
        }
        
        // Otherwise, it's setting a value (like adding a property or item to array via index)
        // If it's an array push via index (rare in patches, usually we push to array path),
        // we handle standard set.
        _set(obj, path, value);
        return;
    }

    if (op === 'DELETE') {
        _unset(obj, path);
        return;
    }

    if (op === 'UPDATE' || op === 'IMPORT') {
        _set(obj, path, value);
    }
};

const applyActionToSchema = (schema: DashboardSchema, action: DashboardAction): DashboardSchema => {
    // Deep clone to ensure immutability
    const newSchema = JSON.parse(JSON.stringify(schema || { kpi: [], chart: [] })) as DashboardSchema;
    if (!newSchema.kpi) newSchema.kpi = [];
    if (!newSchema.chart) newSchema.chart = [];

    if (action.type === 'BATCH_PATCH') {
        action.payload.forEach(op => {
            applyPatch(newSchema, op);
        });
    }
    
    return newSchema;
};

export const createDashboardProvider = () => {
    const [messages, setMessages] = useState<MessageType[]>([]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedVersion, setSelectedVersion] = useState<Dashboard | null>(null);
    const [history, setHistory] = useState<MessageType[]>([]);
    const [versions, setVersions] = useState<Dashboard[]>([]);
    const [isLoadingVersions, setIsLoadingVersions] = useState(false);
    const [showGenerating, setShowGenerating] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const abortControllerRef = useRef<AbortController | null>(null);
    const createNewAbortController = useCallback(() => {
        const controller = new AbortController();
        abortControllerRef.current = controller;
        return controller;
    }, []);

    const [conversations, setConversations] = useState<AgentConversation[]>([]);
    const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
    const [isLoadingConversations, setIsLoadingConversations] = useState(false);
    const [isLoadingMessages, setIsLoadingMessages] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [localSchema, setLocalSchema] = useState<DashboardSchema | undefined>();
    const [activeItemId, setActiveItemId] = useState<string | null>(null);

    // Editor State
    const [editorStates, setEditorStates] = useState<Record<string, EditorState>>({});
    const editorStatesRef = useRef(editorStates);
    useEffect(() => {
        editorStatesRef.current = editorStates;
    }, [editorStates]);

    const initEditor = useCallback((dashboardId: string, schema: DashboardSchema) => {
        console.log('initEditor called', {
            dashboardId,
            kpiCount: schema?.kpi?.length,
            chartCount: schema?.chart?.length,
            alreadyExists: !!editorStates[dashboardId]
        });
        setEditorStates(prev => {
            // Always allow re-init if we want to reset? No, only if not exists.
            // If we switch back to a dashboard, we want to keep state.
            if (prev[dashboardId]) {
                console.log('initEditor skipped - already exists', dashboardId);
                return prev;
            }
            console.log('initEditor creating new state', dashboardId);
            return {
                ...prev,
                [dashboardId]: {
                    originalSchema: schema,
                    currentSchema: schema,
                    actions: [],
                    pointer: -1,
                    isDirty: false
                }
            };
        });
    }, [editorStates]);

    const updateData = useCallback((dashboardId: string, schema: DashboardSchema) => {
        const oldCount = editorStates[dashboardId]?.currentSchema?.kpi?.length;
        console.log('updateData called', {
            dashboardId,
            oldKpiCount: oldCount,
            newKpiCount: schema?.kpi?.length,
            chartCount: schema?.chart?.length,
            stackTrace: new Error().stack?.split('\n').slice(1, 4).join('\n')
        });
        setEditorStates(prev => {
            const state = prev[dashboardId];
            if (!state) {
                console.warn('updateData - state not found for dashboardId:', dashboardId);
                return prev;
            }

            console.log('updateData - updating currentSchema', {
                dashboardId,
                oldCount: state.currentSchema?.kpi?.length,
                newCount: schema?.kpi?.length
            });

            // When updating data (e.g. refresh), we only update the current schema
            // We DO NOT add an action to history, so Undo/Redo is unaffected
            // However, we must ensure that if we Undo later, the replayed actions
            // are applied on top of the ORIGINAL schema, not this new data.
            // Wait, if data changes, `originalSchema` might be stale if it contains data?
            // Actually, data is part of the schema.
            // If we change data, and then Undo a layout change, we want the OLD layout but NEW data?
            // That is tricky with the current Reducer model which replays from `originalSchema`.

            // Solution:
            // 1. Update `originalSchema` with new data? No, that would clear history implicitly if not careful.
            // 2. We treat Data Update as a "base update".
            // We can update `originalSchema` AND re-apply all actions on top of the new `originalSchema`.
            // This assumes actions (Layout/Add/Delete) are compatible with the new data schema.
            // Usually yes, as data refresh just updates `data` fields, preserving IDs.

            // Let's try updating originalSchema and re-computing currentSchema.

            const newOriginalSchema = JSON.parse(JSON.stringify(schema)) as DashboardSchema; // This has new data
            // We need to merge existing actions on top of it.
            // But wait, `schema` passed here is likely `currentSchema` with new data.
            // If we use it as `originalSchema`, we effectively "commit" all previous actions?
            // Yes, if we set `originalSchema = schema` and `actions = []`, we clear history.
            // If user wanted "Update Data" to be transparent, we should probably
            // inject the new data into `originalSchema` but KEEP actions?

            // If `schema` is just `currentSchema` + new data, then it already HAS the layout changes.
            // If we set `currentSchema = schema`, we are fine for display.
            // But if we Undo, we go back to `originalSchema` (old data) + subset of actions.
            // So Undo would revert Data too. That might be acceptable?
            // User: "update data 不需要记录" -> means "Undo should not revert Data"?
            // Or "Data update shouldn't appear in Undo stack as a step"?
            // If it doesn't appear as a step, but we want to KEEP the data after Undo,
            // we must update `originalSchema` to have the new data.

            // BUT `originalSchema` might differ from `currentSchema` in Layout.
            // We can't just swap `originalSchema` with `schema` (which has new layout).

            // Correct approach for "Transparent Data Update":
            // 1. Update `currentSchema` directly (so user sees new data).
            // 2. Update `originalSchema`'s DATA fields only (so Undo uses new data).
            // 3. Update `actions`? No, actions are structural.

            // Simplification: Just update `currentSchema`.
            // If user Undoes, they lose the data refresh. This is consistent.
            // BUT User said "不需要记录" (Don't record).
            // This implies: modifying data shouldn't create a new history entry.
            // So `pointer` stays same. `actions` stays same.
            // Just `currentSchema` changes.
            // IMPLICATION: If I Undo now, I revert to state computed from `originalSchema` + `actions[0..p-1]`.
            // That computed state has OLD data.
            // So "Undo" will implicitly "Undo Data Refresh" too.
            // This is arguably correct behavior for a "Time Travel" undo.
            // If we want to PERSIST data across undo, it's much harder (requires separating Data vs Layout state).

            // For now, let's implement the simple "Update Current State Only" approach.
            // It satisfies "No History Entry".

            return {
                ...prev,
                [dashboardId]: {
                    ...state,
                    currentSchema: schema
                }
            };
        });
    }, [editorStates]);

    const dispatchAction = useCallback((dashboardId: string, action: Omit<DashboardAction, 'timestamp'>) => {
        console.log('dispatchAction', {
            dashboardId,
            action
        })
        setEditorStates(prev => {
            const state = prev[dashboardId];
            console.log('dispatchAction - editorStates check', {
                dashboardId,
                stateExists: !!state,
                availableIds: Object.keys(prev),
                state: state ? 'found' : 'NOT FOUND - dispatch will fail silently'
            });
            if (!state) return prev;

            const timestamp = Date.now();
            const lastAction = state.actions[state.pointer];
            
            // Merge logic:
            // If the new action is a "Layout changed" event,
            // AND the last action was recent (e.g. < 2000ms),
            // AND the last action was a structural change (ADD or DELETE),
            // THEN merge this layout update into the last action.
            
            const isLayoutChange = action.description === 'Layout changed';
            let shouldMerge = false;
            
            if (isLayoutChange && lastAction) {
                const timeDiff = timestamp - lastAction.timestamp;
                if (timeDiff < 2000) {
                    const hasStructuralChange = lastAction.payload.some(op => op.op === 'ADD' || op.op === 'DELETE');
                    if (hasStructuralChange) {
                        shouldMerge = true;
                    }
                }
            }

            if (shouldMerge) {
                const mergedAction: DashboardAction = {
                    ...lastAction,
                    payload: [...lastAction.payload, ...action.payload],
                    timestamp: timestamp // Update timestamp to extend the window if needed
                };
                
                const newActions = [...state.actions];
                // Since we are at state.pointer (tip of effective history for this merge), update it
                newActions[state.pointer] = mergedAction;
                
                // Apply ONLY the NEW operations to the current schema
                const newSchema = applyActionToSchema(state.currentSchema, { ...action, timestamp } as DashboardAction);
                
                console.log('dispatchAction - updating currentSchema', {
                    dashboardId,
                    oldCount: state.currentSchema?.kpi?.length,
                    newCount: newSchema?.kpi?.length,
                    action: action.description
                });
                return {
                    ...prev,
                    [dashboardId]: {
                        ...state,
                        actions: newActions,
                        currentSchema: newSchema,
                        isDirty: true
                    }
                };
            }

            const newAction = { ...action, timestamp: timestamp };
            // Truncate history if we are in the middle
            const newActions = state.actions.slice(0, state.pointer + 1);
            newActions.push(newAction);

            const newSchema = applyActionToSchema(state.currentSchema, newAction);

            console.log('dispatchAction - updating currentSchema (non-merge)', {
                dashboardId,
                oldCount: state.currentSchema?.kpi?.length,
                newCount: newSchema?.kpi?.length,
                action: action.description
            });

            return {
                ...prev,
                [dashboardId]: {
                    ...state,
                    actions: newActions,
                    pointer: newActions.length - 1,
                    currentSchema: newSchema,
                    isDirty: true
                }
            };
        });
    }, []);

    const undo = useCallback((dashboardId: string) => {
        setEditorStates(prev => {
            const state = prev[dashboardId];
            if (!state || state.pointer < 0) return prev;

            // Check if current action is IMPORT
            const currentAction = state.actions[state.pointer];
            const isImport = currentAction?.payload.some(op => op.op === 'IMPORT');
            if (isImport) {
                return prev;
            }

            const newPointer = state.pointer - 1;
            let schema = JSON.parse(JSON.stringify(state.originalSchema));
            for (let i = 0; i <= newPointer; i++) {
                schema = applyActionToSchema(schema, state.actions[i]);
            }

            console.log('undo - updating currentSchema', {
                dashboardId,
                oldCount: state.currentSchema?.kpi?.length,
                newCount: schema?.kpi?.length,
                pointer: newPointer
            });

            return {
                ...prev,
                [dashboardId]: {
                    ...state,
                    pointer: newPointer,
                    currentSchema: schema,
                    isDirty: newPointer >= -1 // Dirty if we have any applied actions?
                    // Actually, if we undo everything, we are back to original, so not dirty.
                    // But if we saved, originalSchema changed.
                    // So dirty check is: is currentSchema different from originalSchema?
                    // Simple check: pointer > -1 implies diff from originalSchema (assuming actions are not no-ops).
                }
            };
        });
    }, []);

    const redo = useCallback((dashboardId: string) => {
        setEditorStates(prev => {
            const state = prev[dashboardId];
            if (!state || state.pointer >= state.actions.length - 1) return prev;

            const newPointer = state.pointer + 1;
            const action = state.actions[newPointer];
            const schema = applyActionToSchema(state.currentSchema, action);

            console.log('redo - updating currentSchema', {
                dashboardId,
                oldCount: state.currentSchema?.kpi?.length,
                newCount: schema?.kpi?.length,
                pointer: newPointer
            });

            return {
                ...prev,
                [dashboardId]: {
                    ...state,
                    pointer: newPointer,
                    currentSchema: schema,
                    isDirty: true
                }
            };
        });
    }, []);

    const resetEditor = useCallback((dashboardId: string) => {
        setEditorStates(prev => {
            const state = prev[dashboardId];
            if (!state) return prev;
            return {
                ...prev,
                [dashboardId]: {
                    ...state,
                    currentSchema: state.originalSchema,
                    actions: [],
                    pointer: -1,
                    isDirty: false
                }
            };
        });
    }, []);

    const saveDashboard = useCallback(async (dashboardId: string, title?: string, conversationId?: string) => {
        const state = editorStates[dashboardId];
        // 如果是临时dashboard，可能没有dirty状态（如果是刚初始化的），但我们也应该允许保存（创建）
        const isPersisted = versions.some(v => v.id === dashboardId);

        // 检查是否需要保存：要么是新的dashboard，要么schema有变化，要么标题有变化
        const currentDashboard = versions.find(v => v.id === dashboardId);
        const hasTitleChange = title && currentDashboard && title !== currentDashboard.title;
        if (isPersisted && (!state || !state.isDirty) && !hasTitleChange) return;

        const currentSchema = state?.currentSchema || versions.find(v => v.id === dashboardId)?.schema;

        try {
            if (!isPersisted) {
                // Create new dashboard
                const sortKey = Date.now();
                const newTitle = title || 'New Dashboard';

                const { data, error } = await supabase
                    .from('dashboard')
                    .insert([{
                        title: newTitle,
                        schema: currentSchema || { kpi: [], chart: [] },
                        sort_key: sortKey
                    }])
                    .select()
                    .single();

                if (error) throw error;

                // 关联会话到仪表板
                if (conversationId) {
                    const { error: convError } = await supabase
                        .from('dashboard_gen_conversations')
                        .upsert({
                            dashboard_id: data.id,
                            id: conversationId
                        }, { onConflict: 'id' });

                    if (convError) {
                        console.error("Failed to link conversation to dashboard:", convError);
                    }
                }

                const newDashboard: Dashboard = {
                    id: data.id,
                    title: data.title,
                    schema: typeof data.schema === 'string' ? JSON.parse(data.schema) : data.schema,
                    created_at: data.created_at,
                    dateRange: {
                        startDate: dayjs().subtract(29, 'day').toDate(),
                        endDate: dayjs().toDate(),
                    }
                };

                // Update versions: replace temp dashboard with new one (actually we should add it if it wasn't there? No, we likely added it to versions manually in UI?)
                // Wait, in previous logic, we did NOT add temp dashboard to `versions`.
                // So here we should ADD it to versions.
                // BUT, if we used UUID for temp ID, we might want to REPLACE if we had put it in versions?
                // The current implementation of `selectTemplate` sets `selectedVersion` but does NOT update `versions`.
                // So `isPersisted` check (versions.some) is correct: it will be false for temp dashboard.

                setVersions(prev => [newDashboard, ...prev]);

                // Update selectedVersion
                setSelectedVersion(newDashboard);

                // Update editorStates: migrate state from temp ID to new ID
                setEditorStates(prev => {
                    const newState = { ...prev };
                    if (newState[dashboardId]) {
                        newState[data.id] = {
                            ...newState[dashboardId],
                            originalSchema: newDashboard.schema, // Reset original to current
                            actions: [],
                            pointer: -1,
                            isDirty: false
                        };
                        delete newState[dashboardId];
                    }
                    return newState;
                });

                return data.id;

            } else {
                // Update existing dashboard
                const updates: any = {};
                if (state && state.isDirty) {
                    updates.schema = state.currentSchema;
                }
                if (title) {
                    updates.title = title;
                }

                if (Object.keys(updates).length === 0) return;

                const { error } = await supabase
                    .from('dashboard')
                    .update(updates)
                    .eq('id', dashboardId);

                if (error) throw error;

                // 关联会话到仪表板（更新时也可以关联）
                if (conversationId) {
                    const { error: convError } = await supabase
                        .from('dashboard_gen_conversations')
                        .upsert({
                            dashboard_id: dashboardId,
                            id: conversationId
                        }, { onConflict: 'id' });

                    if (convError) {
                        console.error("Failed to link conversation to dashboard:", convError);
                    }
                }

                const newSchema = updates.schema || state?.currentSchema;

                setEditorStates(prev => {
                    if (!prev[dashboardId]) return prev;
                    return {
                        ...prev,
                        [dashboardId]: {
                            ...prev[dashboardId],
                            originalSchema: newSchema,
                            actions: [],
                            pointer: -1,
                            isDirty: false
                        }
                    };
                });

                setVersions(prev => prev.map(v =>
                    v.id === dashboardId ? { ...v, ...updates } : v));

                setSelectedVersion(prev => {
                    if (prev && prev.id === dashboardId) {
                        return { ...prev, ...updates };
                    }
                    return prev;
                });
            }

        } catch (e) {
            console.error("Failed to save dashboard:", e);
            setError("Failed to save changes");
        }
    }, [editorStates, versions]);

    // Realtime subscription for conversations
    useEffect(() => {
        let channel: any = null;

        const setupSubscription = async () => {

            channel = supabase
                .channel(`dashboard_conversations`)
                .on(
                    'postgres_changes',
                    {
                        event: '*',
                        schema: 'public',
                        table: 'dashboard_gen_conversations',
                    },
                    (payload) => {
                        if (payload.eventType === 'INSERT') {
                            const newConv = payload.new as AgentConversation;
                            setConversations((prev) => {
                                if (prev.some(c => c.id === newConv.id)) return prev;
                                return [newConv, ...prev];
                            });
                        } else if (payload.eventType === 'UPDATE') {
                            const updatedConv = payload.new as AgentConversation;
                            setConversations((prev) => 
                                prev.map((c) => (c.id === updatedConv.id ? updatedConv : c)));
                        } else if (payload.eventType === 'DELETE') {
                            const deletedId = payload.old.id;
                            setConversations((prev) => 
                                prev.filter((c) => c.id !== deletedId));
                        }
                    }
                )
                .subscribe();
        };

        setupSubscription();

        return () => {
            if (channel) {
                supabase.removeChannel(channel);
            }
        };
    }, []);

    const getConversations = useCallback(async (dashboardId?: string) => {
        setIsLoadingConversations(true);
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            let query = supabase
                .from('dashboard_gen_conversations')
                .select('*')
                .eq('user_id', user.id);

            // 如果提供了 dashboardId，只加载与该 dashboard 关联的 conversation
            if (dashboardId) {
                query = query.eq('dashboard_id', dashboardId);
            }

            const { data, error } = await query.order('updated_at', { ascending: false });

            if (error) throw error;
            setConversations(data || []);
        } catch (error) {
            console.error("Error fetching conversations:", error);
        } finally {
            setIsLoadingConversations(false);
        }
    }, []);

    const createConversation = useCallback(async (title: string = 'New Dashboard') => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return null;

            const { data, error } = await supabase
                .from('dashboard_gen_conversations')
                .insert([{ title, user_id: user.id }])
                .select()
                .single();
            
            if (error) throw error;
            
            setConversations(prev => {
                if (prev.some(c => c.id === data.id)) return prev;
                return [data, ...prev];
            });
            setCurrentConversationId(data.id);
            setMessages([]);
            return data.id;
        } catch (error) {
            console.error("Error creating conversation:", error);
            return null;
        }
    }, []);

    const deleteConversation = useCallback(async (id: string) => {
        try {
            const { error } = await supabase
                .from('dashboard_gen_conversations')
                .delete()
                .eq('id', id);
            
            if (error) throw error;
            
            setConversations(prev => prev.filter(c => c.id !== id));
            if (currentConversationId === id) {
                setCurrentConversationId(null);
                setMessages([]);
            }
        } catch (error) {
            console.error("Error deleting conversation:", error);
        }
    }, [currentConversationId]);

    const updateConversation = useCallback(async (id: string, title: string) => {
        try {
            const { error } = await supabase
                .from('dashboard_gen_conversations')
                .update({ title, updated_at: new Date().toISOString() })
                .eq('id', id);
            
            if (error) throw error;
            
            setConversations(prev => prev.map(c => c.id === id ? { ...c, title } : c));
        } catch (error) {
            console.error("Error updating conversation:", error);
        }
    }, []);

    const getMessages = useCallback(async (conversationId: string) => {
        if (!conversationId || conversationId === 'temp') return;
        setIsLoadingMessages(true);
        try {
            const { data, error } = await supabase
                .from('dashboard_gen_messages')
                .select('*')
                .eq('conversation_id', conversationId)
                .order('created_at', { ascending: true });
            
            if (error) throw error;
            
            // Parse raw_data which contains the array of messages for each run
            const allMessages: MessageType[] = [];
            
            data.forEach((row: any) => {
                try {
                    // raw_data is stored as JSONB, so it might already be an object/array in JS
                    // If it's a string, parse it. If it's already an object, use it.
                    let messages = row.raw_data;
                    if (typeof messages === 'string') {
                        messages = JSON.parse(messages);
                    }
                    
                    if (Array.isArray(messages)) {
                        allMessages.push(...messages);
                    } else if (messages && typeof messages === 'object') {
                        // If it's a single message object
                        allMessages.push(messages);
                    }
                } catch (e) {
                    console.error("Error parsing message raw_data:", e);
                }
            });
            
            // Sort by created_at just in case
            allMessages.sort((a, b) => {
                return new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
            });

            setMessages(allMessages);
        } catch (error) {
            console.error("Error fetching messages:", error);
        } finally {
            setIsLoadingMessages(false);
        }
    }, []);

    // 当切换 dashboard 时，自动加载关联的 conversation 和 messages
    useEffect(() => {
        const dashboardId = selectedVersion?.id;

        // 如果是临时 dashboard 或没有 dashboard，清空状态
        if (!dashboardId || dashboardId === 'temp') {
            setConversations([]);
            setCurrentConversationId(null);
            setMessages([]);
            return;
        }

        // 加载该 dashboard 关联的 conversations
        const loadDashboardConversations = async () => {
            setIsLoadingConversations(true);
            try {
                const { data: { user } } = await supabase.auth.getUser();
                if (!user) return;

                const { data, error } = await supabase
                    .from('dashboard_gen_conversations')
                    .select('*')
                    .eq('dashboard_id', dashboardId)
                    .eq('user_id', user.id)
                    .order('updated_at', { ascending: false });

                if (error) throw error;

                const conversations = data || [];
                setConversations(conversations);

                // 如果有 conversations，选中第一个并加载其消息
                if (conversations.length > 0) {
                    const firstConv = conversations[0];
                    setCurrentConversationId(firstConv.id);
                    // getMessages 会异步加载消息
                    await getMessages(firstConv.id);
                } else {
                    // 如果没有 conversations，清空状态
                    setCurrentConversationId(null);
                    setMessages([]);
                }
            } catch (error) {
                console.error("Error loading dashboard conversations:", error);
                setConversations([]);
                setCurrentConversationId(null);
                setMessages([]);
            } finally {
                setIsLoadingConversations(false);
            }
        };

        loadDashboardConversations();
    }, [selectedVersion?.id, getMessages]); // 只依赖 dashboard ID 和 getMessages


    // // 正在创建时，监听 messages 变化，更新 versions
    // useEffect(() => {
    //     if(messages.length === 0) return;
        
    //     // Check for relevant events
    //     const hasRelevantEvents = messages.some(msg => 
    //         msg.event_type === EVENT_TYPES.TOOL_COMPLETED
    //         || msg.event_type === EVENT_TYPES.AGENT_COMPLETED);

    //     if (!hasRelevantEvents) return;

    //     const dashboard = getSchema(messages, messages[0]?.created_at, messages[0]?.conversation_id || '');
    //     if(dashboard) {
    //         setSelectedVersion(prev => {
    //             // If we are working on the same conversation, or appending to current
    //             // Actually `dashboard.conversation_id` comes from the message.
                
    //             if (prev) {
    //                 // We merge new items into previous schema
    //                 const prevKpiMap = new Map((prev.schema.kpi || []).map(k => [k.id, k]));
    //                 const prevChartMap = new Map((prev.schema.chart || []).map(c => [c.id, c]));

    //                 const finalKpis = (dashboard.schema.kpi || []).map(k => {
    //                     if (!k.id) return k;
    //                     const prevItem = prevKpiMap.get(k.id);
    //                     if (prevItem) {
    //                         return { ...k, layout: prevItem.layout };
    //                     }
    //                     return k;
    //                 });

    //                 const finalCharts = (dashboard.schema.chart || []).map(c => {
    //                     if (!c.id) return c;
    //                     const prevItem = prevChartMap.get(c.id);
    //                     if (prevItem) {
    //                         return { ...c, layout: prevItem.layout };
    //                     }
    //                     return c;
    //                 });

    //                 return { 
    //                     ...prev, 
    //                     schema: {
    //                         kpi: finalKpis,
    //                         chart: finalCharts
    //                     },
    //                     // Only update title if it changed from default
    //                     title: dashboard.title !== 'Dashboard' ? dashboard.title : prev.title,
    //                     dateRange: dashboard.dateRange
    //                 };
    //             }
                
    //             // If no prev (shouldn't happen if we selected a version before starting), just return new
    //             return dashboard;
    //         });
            
    //         // If completed, ensure it's in versions list
    //         if (messages.find(m => m.event_type === EVENT_TYPES.AGENT_COMPLETED)) {
    //             setVersions(prevVersions => {
                    
                    
    //                 return prevVersions;
    //             });
    //         }
    //     }
    // }, [messages])

    const addMessage = useCallback(async (message: MessageType) => {
        setMessages(prevMessages => [...prevMessages, message]);

        // Proactively update dashboard state for better responsiveness
        // This ensures loading states and data updates appear immediately without waiting for the full schema rebuild
        const dashboardId = selectedVersion?.id;
        if (!dashboardId) return;

        if (message.event_type === EVENT_TYPES.TOOL_COMPLETED) {
            const toolMsg = message as any;
            const result = toolMsg.output_struct?.success ? toolMsg.output_struct.result : {};
            const id = result.id;
            const action = result.action || 'add';
            
            if (!id) return;

            if (toolMsg.tool_name === 'kpi') {
                const newKpi: KPI = {
                    id: id,
                    title: 'New KPI',
                    sql: '',
                    formula: '',
                    column: '',
                    format: 'number',
                    unit: '',
                    data: [],
                    loading: false,
                    ...result
                };

                let op: OperationType = 'ADD';
                let path = 'kpi';
                
                if (action === 'update' || action === 'delete') {
                    const currentSchema = editorStates[dashboardId]?.currentSchema;
                    const idx = currentSchema?.kpi?.findIndex((k: any) => k.id === id) ?? -1;

                    if (idx !== -1) {
                        path = `kpi[${idx}]`;
                        op = action === 'delete' ? 'DELETE' : 'UPDATE';
                    } else {
                        // If updating but KPI not found, log warning and skip
                        // This prevents duplicate KPIs with same ID
                        if (action === 'update') {
                            console.warn(`KPI with id ${id} not found for update, skipping to avoid duplicate`);
                            return;
                        }
                        // For delete, just skip silently
                        return;
                    }
                }
                if (op === 'DELETE') {
                    setActiveItemId(id);
                    // Wait for scroll before deleting
                    await new Promise(resolve => setTimeout(resolve, 1000));
                } else {
                    setActiveItemId(id);
                }

                dispatchAction(dashboardId, {
                    type: 'BATCH_PATCH',
                    payload: [{
                        op: op,
                        path: path,
                        value: op === 'DELETE' ? undefined : newKpi
                    }],
                    description: `KPI ${action.toUpperCase()}`
                });
            } else if (toolMsg.tool_name === 'chart') {
                const newChart: Chart = {
                    id: id,
                    title: 'New Chart',
                    sql: '',
                    formula: '',
                    data: [],
                    chart_config: {},
                    loading: false,
                    ...result
                };

                let op: OperationType = 'ADD';
                let path = 'chart';

                if (action === 'update' || action === 'delete') {
                    const currentSchema = editorStates[dashboardId]?.currentSchema;
                    const idx = currentSchema?.chart?.findIndex((c: any) => c.id === id) ?? -1;

                    if (idx !== -1) {
                        path = `chart[${idx}]`;
                        op = action === 'delete' ? 'DELETE' : 'UPDATE';
                    } else {
                        // If updating but chart not found, log warning and skip
                        // This prevents duplicate charts with same ID
                        if (action === 'update') {
                            console.warn(`Chart with id ${id} not found for update, skipping to avoid duplicate`);
                            return;
                        }
                        // For delete, just skip silently
                        return;
                    }
                }

                if (op === 'DELETE') {
                    setActiveItemId(id);
                    // Wait for scroll before deleting
                    await new Promise(resolve => setTimeout(resolve, 1000));
                } else {
                    setActiveItemId(id);
                }

                dispatchAction(dashboardId, {
                    type: 'BATCH_PATCH',
                    payload: [{
                        op: op,
                        path: path,
                        value: op === 'DELETE' ? undefined : newChart
                    }],
                    description: `Chart ${action.toUpperCase()}`
                });
            }
        }
    }, [dispatchAction, selectedVersion?.id, editorStates]);

    const cancelCurrentRequest = useCallback(() => {
        if (abortControllerRef.current && isGenerating) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
            addMessage({
                conversation_id: `dashboard_${Date.now()}`,
                created_at: new Date().toISOString(),
                event_type: EVENT_TYPES.SYSTEM_MESSAGE_DELTA,
                role: ROLES.SYSTEM,
                status: STATUSES.STREAMING,
                agent_type: AGENT_TYPE.AGENT_MODE,
                delta: '',
                content: 'Dashboard generation cancelled'
            });
            setIsGenerating(false);
        }
    }, [isGenerating, addMessage]);

    const getSchema = (rawData: MessageType[], created_at: string, conversation_id: string) => {
        const kpiMap = new Map<string, KPI>();
        const chartMap = new Map<string, Chart>();

        rawData.forEach(msg => {
            if (msg.event_type === EVENT_TYPES.TOOL_COMPLETED) {
                const toolMsg = msg as any;
                const result = toolMsg.output_struct?.success ? toolMsg.output_struct.result : {};
                const id = result.id;
                const action = result.action || 'Add';

                if (!id) return;

                if (toolMsg.tool_name === 'kpi') {
                    if (action === 'Delete') {
                        kpiMap.delete(id);
                    } else {
                        const existing = kpiMap.get(id);
                        kpiMap.set(id, {
                            id: id,
                            title: 'New KPI',
                            sql: '',
                            formula: '',
                            column: '',
                            format: 'number',
                            unit: '',
                            data: [],
                            loading: false,
                            ...existing,
                            ...result
                        });
                    }
                } else if (toolMsg.tool_name === 'chart') {
                    if (action === 'Delete') {
                        chartMap.delete(id);
                    } else {
                        const existing = chartMap.get(id);
                        chartMap.set(id, {
                            id: id,
                            title: 'New Chart',
                            sql: '',
                            formula: '',
                            data: [],
                            chart_config: {},
                            loading: false,
                            ...existing,
                            ...result
                        } as Chart);
                    }
                }
            }
        });

        const kpiResults = Array.from(kpiMap.values());
        const chartResults = Array.from(chartMap.values());

        let dashboard: Dashboard | null = null;

        const title = rawData.find((msg: MessageType) => 
            msg.event_type === EVENT_TYPES.AGENT_START)?.content || 'Dashboard';
        const jsonContent = JSON.parse(rawData.find((msg: MessageType) => 
            msg.event_type === EVENT_TYPES.USER_MESSAGE)?.json_content || '{}');
        const dateRange = {
            startDate: new Date(jsonContent?.dateRange?.startDate || ''),
            endDate: new Date(jsonContent?.dateRange?.endDate || ''),
        };

        if (kpiResults.length > 0 || chartResults.length > 0) {
            dashboard = {
                title,
                schema: { 
                    kpi: kpiResults, 
                    chart: chartResults 
                },
                created_at: created_at,
                conversation_id: conversation_id,
                dateRange: dateRange,
            };
        }
        
        // If we are in the middle of generation (no results yet but started), we still want to return a dashboard structure if possible
        // But the original code returned null if no results.
        // However, if we have "loading" items, kpiResults/chartResults will be non-empty.
        // So this logic holds.

        if (!dashboard && (kpiMap.size > 0 || chartMap.size > 0)) {
            dashboard = {
                title,
                schema: {
                    kpi: kpiResults,
                    chart: chartResults
                },
                created_at: created_at,
                conversation_id: conversation_id,
                dateRange: dateRange,
            };
        }

        if (!dashboard) return null;


        return {...dashboard,} as Dashboard
    }

    const getDashboards = async () => {
        try {
            setIsLoadingVersions(true);
            let { data, error } = await supabase
                .from('dashboard')
                .select('*')
                .order('sort_key', { ascending: false });
            if (error) {
                console.error('Error fetching dashboards:', error);
                setIsLoadingVersions(false);
                return;
            }

            const defaultRange = {
                startDate: dayjs().subtract(29, 'day').toDate(),
                endDate: dayjs().toDate(),
            };
            const mapped: Dashboard[] = (data || []).map((row: any) => ({
                id: String(row.id),
                title: row.title || 'Dashboard',
                schema: typeof row.schema === 'string' ? JSON.parse(row.schema) : row.schema,
                created_at: row.created_at,
                dateRange: defaultRange,
                conversation_id: row.conversation_id || undefined,
            }));
            setVersions(mapped);
            setSelectedVersion(mapped[0] || null);
        } finally {
            setIsLoadingVersions(false);
        }
    };

    const deleteDashboard = useCallback(async (id: string) => {
        try {
            const { error } = await supabase
                .from('dashboard')
                .delete()
                .eq('id', id);
            
            if (error) throw error;
            
            setVersions(prev => prev.filter(v => v.id !== id));
            if (selectedVersion?.id === id) {
                // Select first available or null
                setVersions(prev => {
                    const next = prev.length > 0 ? prev[0] : null;
                    setSelectedVersion(next);
                    return prev;
                });
            }
        } catch (error) {
            console.error("Error deleting dashboard:", error);
        }
    }, [selectedVersion]);

    const renameDashboard = useCallback(async (id: string, newName: string) => {
        try {
            const { error } = await supabase
                .from('dashboard')
                .update({ title: newName })
                .eq('id', id);
            
            if (error) throw error;
            
            setVersions(prev => prev.map(v => v.id === id ? { ...v, title: newName } : v));
            if (selectedVersion?.id === id) {
                setSelectedVersion(prev => prev ? { ...prev, title: newName } : prev);
            }
        } catch (error) {
            console.error("Error renaming dashboard:", error);
        }
    }, [selectedVersion]);

    const reorderDashboards = useCallback(async (newOrder: Dashboard[], movedId?: string, newIndex?: number) => {
        // Optimistic update
        setVersions(newOrder);
        
        if (!movedId || newIndex === undefined || newIndex === null) {
            console.warn("Missing movedId or newIndex for reorder, fallback to full reorder not implemented");
            return;
        }

        // Calculate new sort_key
        // Note: newOrder is ALREADY sorted by the user's drag.
        // But the sort_keys inside are stale (from before the drag).
        
        const prevItem = newOrder[newIndex - 1];
        const nextItem = newOrder[newIndex + 1];
        
        // We assume Descending order (higher key at top)
        // If moved to Top (index 0), newKey > nextItem.key
        // If moved to Bottom (index N), newKey < prevItem.key
        // If Middle, between prev and next.
        
        // Safety checks for keys. If undefined, we might need fallback.
        // Assuming all items have sort_key populated from DB.
        
        let newSortKey: number;
        const GAP = 1000000;

        if (!prevItem) {
            // Top of list
            // Use Date.now() to ensure it's larger than any existing timestamp-based keys (assuming no future keys)
            // But if we have mixed keys (some small, some timestamps), we should be careful.
            // If the nextItem key is HUGE (future timestamp?), Date.now() might not be enough.
            // Safer: max(Date.now(), nextKey + GAP)
            const nextKey = nextItem?.sort_key ?? 0;
            newSortKey = Math.max(Date.now(), nextKey + GAP);
        } else if (!nextItem) {
            // Bottom of list
            const prevKey = prevItem?.sort_key ?? 0;
            newSortKey = prevKey - GAP;
        } else {
            // Between two items
            const prevKey = prevItem.sort_key || 0;
            const nextKey = nextItem.sort_key || 0;
            
            // If keys are 0 (e.g. old data), we need to handle it gracefully.
            // But if both are 0, we can't really insert between.
            // We should assume if keys are missing/zero, we might need a reset strategy or fallback.
            // For now, if we get 0, let's assume it's just a small number.
            
            if (prevKey === 0 && nextKey === 0) {
                // Edge case: all keys are 0 or null.
                // We should probably just give it a new large timestamp-based key to force it to top/bottom?
                // Or just 0 is fine if we don't care.
                // But wait, if we are in middle, and both neighbors are 0, new key is 0.
                // That means collision.
                // Ideally we should re-index everything if we detect this degeneration.
                // Fallback:
                newSortKey = Date.now();
            } else {
                newSortKey = (prevKey + nextKey) / 2;
            }
        }

        // Update local state sort_key to prevent future jitter/inconsistency
        // Clone to avoid mutation of state objects
        const updatedItem = { ...newOrder[newIndex], sort_key: newSortKey };
        const newOrderWithUpdate = [...newOrder];
        newOrderWithUpdate[newIndex] = updatedItem;
        
        // Force update versions with new key
        setVersions(newOrderWithUpdate); 

        try {
            const { error } = await supabase
                .from('dashboard')
                .update({ sort_key: newSortKey })
                .eq('id', movedId);
                
            if (error) {
                console.error("Supabase update error:", error);
                throw error;
            }

        } catch (e) {
            console.error("Failed to reorder dashboards:", e);
            getDashboards();
        }
    }, []);

    const _showCreateModal = (show: boolean) => {
        if(show && !isGenerating){
            setHistory(prev => [...prev, ...messages])
            setMessages([])
        }
        setShowCreateModal(show);
    }

    useEffect(()=>{
        getDashboards()
    }, [])

  
    return {
        messages,
        setMessages,
        addMessage,
        isGenerating,
        setIsGenerating,
        showGenerating,
        setShowGenerating,
        abortControllerRef,
        cancelCurrentRequest,
        createNewAbortController,
        error,
        setError,
        selectedVersion,
        setSelectedVersion,
        versions,
        setVersions,
        history,
        setHistory,
        isLoadingVersions,
        setIsLoadingVersions,
        
        editorStates,
        initEditor,
        dispatchAction,
        undo,
        redo,
        saveDashboard,
        resetEditor,
        updateData,
        deleteDashboard,
        renameDashboard,
        reorderDashboards,

        showCreateModal,
        setShowCreateModal: _showCreateModal,
        getDashboards,
        conversations,
        setConversations,
        currentConversationId,
        setCurrentConversationId,
        isLoadingConversations,
        isLoadingMessages,
        getConversations,
        createConversation,
        deleteConversation,
        updateConversation,
        getMessages,
        isEditMode,
        setIsEditMode,
        localSchema,
        setLocalSchema,
        activeItemId,
        setActiveItemId
    };
};
